/* eslint-disable no-undefined */
import { Injectable } from '@angular/core';
import TrimbleMaps, { Common } from '@trimblemaps/trimblemaps-js';
import * as DaTrip from '../_models/daws/trip';
import { UserService } from 'src/app/services/user.service';
import { StopLocation, StopState } from '../_models/state/stopState';
import { RouteStop } from '../_models/view/routeStop';
import { optionsUI } from '../utils/optionsUI';
import { RouteState } from '../_models/state/routeState';
import { find } from 'lodash-es';
import { defaultDimensions } from '../utils/defaultDimensions';
import {
  DisplayRestrictionTypeEnum,
  FerriesEnum,
  HazmatTypeEnum,
  RestrictionZonesModel,
  RoutingProfileModel,
  RoutingTypeEnum,
  TollRoadTypeEnum,
  TunnelCategoryEnum,
  UnitsOfMeasurementEnum,
  VehicleTypeEnum,
} from '../_models/profilews/profilews';
import { RoutingProfile } from '../_models/daws/trip';
import { SubscriptionLevel } from '../auth/models/user';
import { SideOfStreetAdherenceLevel } from 'data-access';

@Injectable({ providedIn: 'root' })
export class ConversionService {
  NUM_INCHES_IN_METER = 39.37;
  NUM_POUNDS_IN_KILOGRAM = 2.20462;
  NUM_INCHES_IN_FOOT = 12;
  constructor(private userService: UserService) {}

  // TODO refactor at least the basic functions out
  // It feels like this service is a dumping ground of helper functions
  // why do we need to maintain an entire service just to do stateless conversions?

  feet(wholeFeet): number {
    const feet = Math.floor(wholeFeet);
    return feet;
  }

  feetToString(wholeFeet): string {
    const feet = this.feet(wholeFeet);
    const inches = this.inches(wholeFeet);
    const retval = feet + "'" + inches + '"'; // eslint-disable-line
    return retval;
  }

  inches(wholeFeet): number {
    const feet = Math.floor(wholeFeet);
    const inches = Math.round((wholeFeet - feet) * 12);
    return inches;
  }

  round(value, decimals): number {
    return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals); // eslint-disable-line
  }

  feetInchesToMeters(feet, inches, decimals): number {
    const totalInches = this.NUM_INCHES_IN_FOOT * feet + inches;

    const meters = totalInches / this.NUM_INCHES_IN_METER;

    if (decimals) {
      return this.round(meters, decimals);
    }

    return meters;
  }

  metersToFeetInches(meters, decimals): number[] {
    const inches = parseInt(String(meters * this.NUM_INCHES_IN_METER));
    return [
      parseInt(String(inches / this.NUM_INCHES_IN_FOOT)),
      inches % this.NUM_INCHES_IN_FOOT,
    ];
  }

  stringToFeetInches(str): number[] {
    let nums = str.match(/\d+/g); // array or null returned.
    if (nums === null) {
      nums = ['0', '0'];
    }
    if (nums.length < 2) {
      if (str.indexOf('"')) {
        nums.unshift('0');
      } else {
        nums.push('0');
      }
    }
    return [parseInt(nums[0]), parseInt(nums[1])];
  }

  stringToTotalInches(str): number {
    const feetInches = this.stringToFeetInches(str);
    const feet = feetInches[0];
    const inches = feetInches[1];

    return feet * this.NUM_INCHES_IN_FOOT + inches;
  }

  convertFromCentiMeterToInches(val) {
    return val * 0.3937;
  }
  convertFromInchesToCentiMeter(val) {
    return val * 2.54;
  }

  totalInchesToString(totalInches, keepTotalInches): any {
    if (keepTotalInches === true) {
      const roundedValue = Math.floor(totalInches);
      return `${roundedValue.toString()}"`;
    }

    const feet = Math.floor(totalInches / this.NUM_INCHES_IN_FOOT);
    const inches = Math.floor(totalInches % this.NUM_INCHES_IN_FOOT);
    const feetInches = [feet, inches];

    return this.feetInchesToString(feetInches);
  }

  feetInchesToString(arr): any {
    let feet = arr[0];
    let inches = arr[1];

    if (feet !== '') {
      feet += "'"; // eslint-disable-line
    }
    if (inches !== '') {
      inches += '"';
    }
    return feet + inches;
  }
  isString(value): boolean {
    return typeof value === 'string' || value instanceof String;
  }

  isNumber(n): boolean {
    return /^-?[\d.]+(?:e-?\d+)?$/.test(n);
  }

  poundsToKilograms(str, decimals): number {
    let num;

    if (this.isString(str)) {
      num = parseFloat(str);
    } else if (this.isNumber(str)) {
      num = str;
    }

    const kilograms = num / this.NUM_POUNDS_IN_KILOGRAM;

    if (decimals) {
      return this.round(kilograms, decimals);
    }

    return kilograms;
  }

  kilogramsToPounds(str): number {
    return parseInt(String(parseFloat(str) * this.NUM_POUNDS_IN_KILOGRAM));
  }

  inchesToMeters(inch, decimals): number {
    const m = parseFloat(inch) / this.NUM_INCHES_IN_METER;

    return decimals ? this.round(m, decimals) : m;
  }

  metersToInches(meter): number {
    return parseInt(String(parseFloat(meter) * this.NUM_INCHES_IN_METER));
  }

  bounds(min, max, value): any {
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  }

  convertVehicleType(
    vehicleType: DaTrip.ProfileVehicleType
  ): (typeof TrimbleMaps.Common.VehicleType)[keyof typeof TrimbleMaps.Common.VehicleType] {
    let vt = TrimbleMaps.Common.VehicleType.TRUCK.valueOf();
    if (vehicleType === DaTrip.ProfileVehicleType.Auto) {
      vt = TrimbleMaps.Common.VehicleType.AUTOMOBILE.valueOf();
    } else if (
      vehicleType === DaTrip.ProfileVehicleType.LightDuty ||
      vehicleType === DaTrip.ProfileVehicleType.MidsizeTruck ||
      vehicleType === DaTrip.ProfileVehicleType.Bus
    ) {
      vt = TrimbleMaps.Common.VehicleType.LIGHT_TRUCK.valueOf();
    }
    return vt as (typeof TrimbleMaps.Common.VehicleType)[keyof typeof TrimbleMaps.Common.VehicleType];
  }
  convertToProfileVehicleType(
    vehicleType: VehicleTypeEnum
  ): (typeof DaTrip.ProfileVehicleType)[keyof typeof DaTrip.ProfileVehicleType] {
    switch (vehicleType) {
      case VehicleTypeEnum.Auto:
        return DaTrip.ProfileVehicleType.Auto.valueOf();
        break;
      case VehicleTypeEnum.Bus:
        return DaTrip.ProfileVehicleType.Bus.valueOf();
        break;
      case VehicleTypeEnum.LightDuty:
        return DaTrip.ProfileVehicleType.LightDuty.valueOf();
        break;
      case VehicleTypeEnum.MidsizeTruck:
        DaTrip.ProfileVehicleType.MidsizeTruck.valueOf();
        break;
      case VehicleTypeEnum.Truck:
        DaTrip.ProfileVehicleType.Truck.valueOf();
        break;
      default:
        DaTrip.ProfileVehicleType.Truck.valueOf();
    }
  }

  convertToProfileVehicleTypeEnum(
    vehicleType: DaTrip.ProfileVehicleType
  ): VehicleTypeEnum {
    switch (vehicleType) {
      case DaTrip.ProfileVehicleType.Auto:
        return VehicleTypeEnum.Auto;
        break;
      case DaTrip.ProfileVehicleType.Bus:
        return VehicleTypeEnum.Bus;
        break;
      case DaTrip.ProfileVehicleType.LightDuty:
        return VehicleTypeEnum.LightDuty;
        break;
      case DaTrip.ProfileVehicleType.MidsizeTruck:
        return VehicleTypeEnum.MidsizeTruck;
        break;
      case DaTrip.ProfileVehicleType.Truck:
        return VehicleTypeEnum.Truck;
        break;
      default:
        return VehicleTypeEnum.Truck;
    }
  }
  convertTunnelRestrictionDAtoRouteOption(profile: DaTrip.RoutingProfile) {
    switch (profile.tunnelRestriction) {
      case DaTrip.TunnelRestrictions.None:
        return TrimbleMaps.Common.TunnelCategory.NONE;
        break;
      case DaTrip.TunnelRestrictions.EUTunnelBCDE:
        return TrimbleMaps.Common.TunnelCategory.BCDE;
        break;
      case DaTrip.TunnelRestrictions.EUTunnelCDE:
        return TrimbleMaps.Common.TunnelCategory.CDE;
        break;
      case DaTrip.TunnelRestrictions.EUTunnelDE:
        return TrimbleMaps.Common.TunnelCategory.DE;
        break;
      case DaTrip.TunnelRestrictions.EUTunnelE:
        return TrimbleMaps.Common.TunnelCategory.E;
        break;
      default:
        return TrimbleMaps.Common.TunnelCategory.NONE;
    }
  }
  convertTunnelRestrictionProfiletoDARouteOption(
    selectedProfile: RoutingProfileModel
  ): DaTrip.TunnelRestrictions {
    switch (selectedProfile.vehicleOptions?.tunnelCategory) {
      case TrimbleMaps.Common.TunnelCategory.NONE:
        return DaTrip.TunnelRestrictions.None;
        break;
      case TrimbleMaps.Common.TunnelCategory.BCDE:
        return DaTrip.TunnelRestrictions.EUTunnelBCDE;
        break;
      case TrimbleMaps.Common.TunnelCategory.CDE:
        return DaTrip.TunnelRestrictions.EUTunnelCDE;
        break;
      case TrimbleMaps.Common.TunnelCategory.DE:
        return DaTrip.TunnelRestrictions.EUTunnelDE;
        break;
      case TrimbleMaps.Common.TunnelCategory.E:
        return DaTrip.TunnelRestrictions.EUTunnelE;
        break;
      default:
        return DaTrip.TunnelRestrictions.None;
    }
  }
  // getPCMRouteOptionsFromDA
  convertToRouteOptions(
    profile: DaTrip.RoutingProfile,
    routingProfileId: number
  ): Omit<TrimbleMaps.RouteOptions, 'dataVersion'> & {
    dataVersion: string;
  } {
    const options: Omit<TrimbleMaps.RouteOptions, 'dataVersion'> & {
      dataVersion: string;
    } = {
      // profile.routingProfileId, // Not in SDK Route Options
      // profile.name, // Not in SDK

      // RouteOptionsBase
      profileId: routingProfileId,
      vehicleType: this.convertVehicleType(profile.vehicleType),
      routeType: profile.routingType,
      // routeOptimization?: // not in DA
      // /* @deprecated */
      tollDiscourage: profile.tollDiscouraged ? profile.tollDiscouraged : false,
      tollRoads: profile.tollRoadType,
      bordersOpen: profile.bordersOpen ? profile.bordersOpen : false,
      overrideRestrict: profile.overrideRestrictions,
      highwayOnly: profile.highwayOnly ? profile.highwayOnly : false,
      hazMatType: profile.hazmatType, // 0-8: 0-7
      congestionZone:
        this.userService.getUser()?.region?.toLowerCase() === Common.Region.EU
          ? (profile.congestionZone as unknown as (typeof Common.ZoneType)[keyof typeof Common.ZoneType])
          : undefined,
      // hazMatTypes?: Array<typeof Common.HazMatType[keyof typeof Common.HazMatType]>; // not in DA
      // tunnelCategories:
      //   this.userService.getUser()?.region?.toLowerCase() === Common.Region.EU
      //     ? ([
      //         this.convertTunnelRestrictionDAtoRouteOption(profile),
      //       ] as unknown as Array<
      //         (typeof Common.TunnelCategory)[keyof typeof Common.TunnelCategory]
      //       >)
      //     : undefined,
      distanceUnits: profile.distanceUnits,
      // // TruckCfg
      trkLCV:
        profile.vehicleType !== DaTrip.ProfileVehicleType.Auto
          ? profile.LCV
          : undefined,
      trkUnits: profile.totalLength ? profile.unitsOfMeasure : undefined,
      // trkHeight: // conversion at the end of the function
      // trkLength: // conversion at the end of the function
      // trkWidth: // conversion at the end of the function
      trkWeight: profile.totalWeight,
      trkAxles:
        profile.vehicleType !== DaTrip.ProfileVehicleType.Auto
          ? profile.numAxles
          : undefined,
      // elevLimit: // conversion at the end of the function
      // hosEnabled?: boolean; // not in DA
      // hosRemDriveTimeUntilBreak?: number; // not in DA
      // hosRemDriveTime?: number; // not in DA
      // hosRemOnDutyTime?: number; // not in DA
      // hosRuleType?: typeof Common.HoSScheduleType[keyof typeof Common.HoSScheduleType]; // not in DA
      // hosRemCycleDutyTime?: number; // not in DA
      sideOfStreetAdherence: profile.sideOfStreetAdherence
        ? profile.sideOfStreetAdherence
        : DaTrip.ProfileSideOfStreetAdherenceLevel.Off,
      // governorSpeedLimit: // conversion at the end of the function
      useSites: profile.useSites || false,
      // refrigerated: // conversion at the end of the function
      // fuelType: // conversion at the end of the function
      // fuelConsumption: // conversion at the end of the function
      // avoidState?: Array<string>; // not in DA
      // avoidCountry?: Array<string>; // not in DA
      // favorState?: Array<string>; // not in DA
      // favorCountry?: Array<string>; // not in DA
      // useCrossStreetTurnAvoidance?: boolean; // not in DA
      // // TrailerCfg
      // trlType: TrimbleMaps.Common.TrailerType.TRAILER,
      // trlCount: // conversion at the end of the function
      // trlMaxHt?: // conversion at the end of the function
      // trlMaxWt: // conversion at the end of the function
      // trlMaxAxles: // conversion at the end of the function
      // // TrailerCfg.Engine
      // isHybrid: // conversion at the end of the function
      // emiType: // conversion at the end of the function
      // minPoll: // conversion at the end of the function

      // we only want classOverrides 0(None) or 3(fiftyThreeFootTrailer + nationalNetwork) to be consistent with PCM UI.
      overrideClass:
        profile.classOverrides > 0
          ? TrimbleMaps.Common.ClassOverride.ALL
          : TrimbleMaps.Common.ClassOverride.NONE,
      // // RouteOptions
      // routeId?: // SDK UI
      // routeColor?: // SDK UI
      // routeWidth?: // SDK UI
      // stops?: // In stop options
      inclFerryDist: profile.includeFerryDistance,
      fuelUnits: profile.fuelUnits,
      // truckConfig?: // repeat item
      inclTollData: profile.includeTollData,
      fuelEconLoad: Number.parseFloat(profile.fuelEconomyLoad),
      fuelEconEmpty: Number.parseFloat(profile.fuelEconomyEmpty),
      costPerFuelUnit: Number.parseFloat(profile.costPerFuelUnit),
      costGHG: Number.parseFloat(profile.costGHG),
      costMaintLoad: Number.parseFloat(profile.costMaintLoad),
      costMaintEmpty: Number.parseFloat(profile.costMaintEmpty),
      costTimeLoad: Number.parseFloat(profile.costTimeLoad),
      costTimeEmpty: Number.parseFloat(profile.costTimeEmpty),
      tollCurrency:
        profile.tollCurrency as (typeof TrimbleMaps.Common.TollCurrency)[keyof typeof TrimbleMaps.Common.TollCurrency],
      exchangeRate: Number.parseFloat(profile.exchangeRate),
      tollPlan: profile.includeTollData ? profile.tollDiscountPlans : '',
      // custRdSpeeds?: // not in DA
      // citeInt?: // not in DA
      region: this.userService.getUser()?.region?.toLowerCase(),
      dataVersion: profile.dataVersion,
      // reportType?: // not in DA
      // isDraggable?: // SDK UI
      // dragOptions?: // SDK UI
      // originIcon?: // SDK UI
      // destinationIcon?: // SDK UI
      // stopIcon?: // SDK UI
      // assetId?: // not in DA
      // estimatedTimeOpts?: // not in DA
      // useTraffic: // conversion at the end of the function
      // frameRoute?: // SDK UI
      // showStops?: // SDK UI
      // requestMethod?: // SDK UI
      // frameOptions?: // SDK UI
      // showArrows?: // SDK UI
      // arrowOptions?: // SDK UI
    };
    const vehicleAxles = this.getAxels(options?.vehicleType);

    options.trkAxles =
      options.vehicleType !== Common.VehicleType.AUTOMOBILE
        ? vehicleAxles
        : undefined;
    options.trkMaxWeightPerAxleGroup = profile?.MaxWeightPerAxleGroup;

    const afsetIds = this.userService.getSelectedAFSets()?.map((af) => {
      return af;
    });
    // const afsetNames = this.userService.getSelectedAFSets()?.map((af) => {
    //   return af.name;
    // });
    options.afSetIDs = afsetIds;
    // options.afSetNames = afsetNames;
    options.useAvoidsAndFavors = profile.useAvoidFavors;
    // For new customers, only use unitOfMeasure; for existing customer, fix governorSpeedLimit and elevLimit if 2 units are different.
    if (profile.unitsOfMeasure === 0 && profile.distanceUnits === 1) {
      if (profile.governorSpeedLimit !== null) {
        options.governorSpeedLimit = Math.floor(
          profile?.governorSpeedLimit / 1.61
        );
      }
      if (profile.elevationLimit !== null) {
        options.elevLimit = Math.floor(profile?.elevationLimit / 1.61);
      }
      options.distanceUnits = 0;
    }
    if (profile.unitsOfMeasure === 1 && profile.distanceUnits === 0) {
      if (profile.governorSpeedLimit !== null) {
        options.governorSpeedLimit = Math.floor(
          profile?.governorSpeedLimit * 1.61
        );
      }
      if (profile.elevationLimit !== null) {
        options.elevLimit = Math.floor(profile?.elevationLimit * 1.61);
      }
      options.distanceUnits = 1;
    }
    if (
      profile.unitsOfMeasure === DaTrip.VehicleDimUnits.English &&
      profile.totalLength
    ) {
      const DimOptions = this.convertToEnglish(profile);
      options.trkHeight = DimOptions.trkHeight;
      options.trkLength = DimOptions.trkLength;
      options.trkWidth = DimOptions.trkWidth;
      options.trkWeight = DimOptions.trkWeight;
      if (
        profile?.euProfile &&
        profile.euProfile?.hasTrailer &&
        profile.euProfile?.trailerMaxHt
      ) {
        options.trlMaxHt = this.totalInchesToString(
          profile.euProfile?.trailerMaxHt,
          false
        );
      }
      if (!Array.isArray(profile.totalLength)) {
        options.governorSpeedLimit =
          profile.governorSpeedLimit !== null
            ? profile?.governorSpeedLimit
            : undefined;
        options.elevLimit =
          profile.elevationLimit !== null ? profile?.elevationLimit : undefined;
      }
    } else if (
      profile.unitsOfMeasure === DaTrip.VehicleDimUnits.Metric &&
      profile.totalLength
    ) {
      const DimOptions = this.convertToMetric(profile);
      options.trkHeight = DimOptions.trkHeight;
      options.trkLength = DimOptions.trkLength;
      options.trkWidth = DimOptions.trkWidth;
      options.trkWeight = DimOptions.trkWeight;
      options.governorSpeedLimit = DimOptions?.governorSpeedLimit;
      options.elevLimit = DimOptions?.elevLimit;
      if (
        profile?.euProfile &&
        profile?.euProfile?.hasTrailer &&
        profile.euProfile.trailerMaxHt
      ) {
        options.trlMaxHt = profile.euProfile.trailerMaxHt.toString();
      }
    }
    if (profile.euProfile?.hasTrailer) {
      if (profile.euProfile?.trailerType === DaTrip.EUTrailerType.Caravan) {
        options.trlType = TrimbleMaps.Common.TrailerType.CARAVAN;
      } else if (profile.euProfile.trailerType === DaTrip.EUTrailerType.None) {
        options.trlType = TrimbleMaps.Common.TrailerType.NONE;
      } else if (
        profile.euProfile.trailerType === DaTrip.EUTrailerType.Trailer
      ) {
        options.trlType = TrimbleMaps.Common.TrailerType.TRAILER;
      }
      if (profile.euProfile?.refrigerated !== null) {
        options.refrigerated = profile.euProfile?.refrigerated;
      }
      if (profile.euProfile?.fuelType !== null) {
        options.fuelType = profile.euProfile?.fuelType;
      }
      if (profile.euProfile?.fuelConsumption !== null) {
        options.fuelConsumption = profile.euProfile?.fuelConsumption;
      }
      if (profile.euProfile?.trailerCount !== null) {
        options.trlCount = profile.euProfile?.trailerCount;
      }
      if (profile.euProfile?.trailerMaxWt !== null) {
        options.trlMaxWt = profile.euProfile?.trailerMaxWt;
      }
      if (profile.euProfile?.trailerMaxAxles !== null) {
        options.trlMaxAxles = profile.euProfile?.trailerMaxAxles;
      }
      if (profile.euProfile?.isHybrid !== null) {
        options.isHybrid = profile.euProfile?.isHybrid;
      }
      if (profile.euProfile?.emiType !== null) {
        options.emiType = profile.euProfile?.emiType;
      }
      if (profile.euProfile?.minPoll !== null) {
        options.minPoll = profile.euProfile?.minPoll;
      }
    }
    if (profile.useTraffic !== null) {
      options.useTraffic = profile.useTraffic;
    } else {
      options.useTraffic = false;
    }
    return options;
  }

  // getDARouteOptionsFromPCM
  convertToDARouteOptions(
    routeName: string,
    routingProfileId: number | null,
    options: TrimbleMaps.RouteOptions,
    selectedProfile: RoutingProfileModel,
    euProfile: DaTrip.EUProfile
  ): DaTrip.RoutingProfile {
    const daOptions: DaTrip.RoutingProfile = {
      vehicleType:
        options.vehicleType === TrimbleMaps.Common.VehicleType.AUTOMOBILE
          ? DaTrip.ProfileVehicleType.Auto
          : options.vehicleType === TrimbleMaps.Common.VehicleType.LIGHT_TRUCK
          ? DaTrip.ProfileVehicleType.LightDuty
          : DaTrip.ProfileVehicleType.Truck,
      congestionZone:
        options.congestionZone === TrimbleMaps.Common.ZoneType.ALLOW
          ? 0
          : options.congestionZone === TrimbleMaps.Common.ZoneType.WARN
          ? 1
          : 2,
      // tunnelRestriction:
      //   this.convertTunnelRestrictionProfiletoDARouteOption(selectedProfile),
      environmentalZone: null, // Todo-yz not in the SDK
      LCV: options.trkLCV ? options.trkLCV : false,
      VehicleGroups: [],
      classOverrides:
        options.overrideClass === TrimbleMaps.Common.ClassOverride.ALL
          ? TrimbleMaps.Common.ClassOverride.ALL
          : TrimbleMaps.Common.ClassOverride.NONE,
      costGHG: options.costGHG?.toString(),
      costMaintEmpty: options.costMaintEmpty?.toString(),
      costMaintLoad: options.costMaintLoad?.toString(),
      costPerFuelUnit: options.costPerFuelUnit?.toString(),
      costTimeEmpty: options.costTimeEmpty?.toString(),
      costTimeLoad: options.costTimeLoad?.toString(),
      dataVersion: options.dataVersion,
      useAvoidFavors: options.afSetIDs.length > 0,
      displayRestrictions:
        options.displayRestrictions ===
        TrimbleMaps.Common.DisplayRestrictionsType.ALWAY_OFF
          ? DaTrip.DisplayRestrictionsType.AlwaysOff
          : options.displayRestrictions ===
            TrimbleMaps.Common.DisplayRestrictionsType.ALWAY_ON
          ? DaTrip.DisplayRestrictionsType.AlwaysOn
          : DaTrip.DisplayRestrictionsType.BasedOnTruckRestrictions,
      distanceUnits: options.distanceUnits,
      exchangeRate: options.exchangeRate?.toString(),
      useTraffic: options.useTraffic,
      //ferryClosed: false,
      fiftyThreeFootTrailer:
        options.overrideClass === TrimbleMaps.Common.ClassOverride.ALL,
      fuelEconomyEmpty: options.fuelEconEmpty?.toString(),
      fuelEconomyLoad: options.fuelEconLoad?.toString(),
      fuelUnits: options.fuelUnits,
      hazmatType: options.hazMatType,
      highwayOnly: options.highwayOnly ? options.highwayOnly : false,
      includeFerryDistance: options.inclFerryDist,
      nationalNetwork:
        options.overrideClass === TrimbleMaps.Common.ClassOverride.ALL,
      numAxles: options.trkAxles,
      //propane: true,
      routingProfileId: routingProfileId, // The SDK, PCM doesn't have id. It has profile name.
      tollCurrency: options.tollCurrency,
      tollDiscouraged: options.tollDiscourage ? options.tollDiscourage : false,
      tollRoadType: options.tollRoads || DaTrip.TollRoadsType.AvoidIfPossible, // IS REQUIRED
      // totalWeight: Number(options.trkWeight), // The SDK trkWeight is number. The weight of the vehicle in pounds or kilograms depending on units.
      //totalWeightPerAxle: 0,
      truckDimensions: 16,
      unitsOfMeasure:
        options.distanceUnits === UnitsOfMeasurementEnum.Imperial
          ? DaTrip.VehicleDimUnits.English
          : DaTrip.VehicleDimUnits.Metric,
      name: routeName,
      bordersOpen: options.bordersOpen ? options.bordersOpen : false,
      routingType: options.routeType,
      overrideRestrictions: options.overrideRestrict,
      //hubRouting: options.hubRouting, // The SDK doesn't support hubRouting
      includeTollData: options.inclTollData,
      tollDiscountPlans: options.inclTollData ? options.tollPlan : '', // if toll data is false, put empty string.
      elevationLimit: options.elevLimit, // Can't be null.
      isFerryDiscouraged: false, // Todo-yz: the SDK doesn't have options.ferryDiscourage. After the SDK update, need to update this line.
      useSites: options.useSites,
      governorSpeedLimit: options.governorSpeedLimit, // Can't be null.
      sideOfStreetAdherence: selectedProfile?.routingOptions
        ?.sideOfStreetAdherenceLevel as unknown as DaTrip.ProfileSideOfStreetAdherenceLevel,
      euProfile: {
        fuelType: euProfile?.fuelType,
        refrigerated: euProfile?.refrigerated,
        fuelConsumption: euProfile?.fuelConsumption,
        emiType: euProfile?.emiType,
        hasTrailer: euProfile?.hasTrailer,
        // hasTrailer: // Todo-yz: the SDK doesn't have options.EUOptions.hasTrailer. I don't knwo what to do with it.
        isHybrid: euProfile?.isHybrid,
        trailerCount: euProfile?.trailerCount,
        minPoll: euProfile?.minPoll,
        trailerMaxAxles: euProfile?.trailerMaxAxles,
        trailerType: euProfile
          ? euProfile?.trailerType === TrimbleMaps.Common.TrailerType.CARAVAN
            ? DaTrip.EUTrailerType.Caravan
            : euProfile?.trailerType === TrimbleMaps.Common.TrailerType.TRAILER
            ? DaTrip.EUTrailerType.Trailer
            : DaTrip.EUTrailerType.None
          : undefined,
        trailerMaxWt: euProfile?.trailerMaxWt,
        trailerMaxHt: euProfile?.trailerMaxHt,
      },
    };
    if (selectedProfile?.vehicleOptions?.vehicleConfig) {
      daOptions.maxHeight =
        selectedProfile?.vehicleOptions?.vehicleConfig?.height;
      daOptions.maxWidth =
        selectedProfile?.vehicleOptions?.vehicleConfig?.width;
      daOptions.totalLength =
        selectedProfile?.vehicleOptions?.vehicleConfig?.length;
      daOptions.totalWeight =
        selectedProfile?.vehicleOptions?.vehicleConfig?.weight;
      daOptions.MaxWeightPerAxleGroup =
        selectedProfile?.vehicleOptions?.vehicleConfig?.maxWeightPerAxleGroup;
    }
    if (euProfile && selectedProfile?.vehicleOptions?.vehicleConfig) {
      daOptions.totalWeight = Math.floor(
        this.kilogramsToPounds(daOptions.totalWeight)
      );
      daOptions.maxHeight = Math.floor(
        this.convertFromCentiMeterToInches(daOptions.maxHeight)
      ); // Conversting the cm value to inches Eg:  in=cm*0.39370
      daOptions.totalLength = Math.floor(
        this.convertFromCentiMeterToInches(daOptions.totalLength)
      );
      daOptions.maxWidth = Math.floor(
        this.convertFromCentiMeterToInches(daOptions.maxWidth)
      );
    }
    // The web serivce is very picky on null or undefined values on the following properties.
    // Byte can't be null
    if (daOptions.environmentalZone === null) {
      delete daOptions.environmentalZone;
    }
    if (daOptions.elevationLimit === null || isNaN(daOptions.elevationLimit)) {
      delete daOptions.elevationLimit;
    }
    if (daOptions.totalWeight === null || isNaN(daOptions.totalWeight)) {
      delete daOptions.totalWeight;
    }
    if (
      daOptions.governorSpeedLimit === null ||
      isNaN(daOptions.governorSpeedLimit)
    ) {
      delete daOptions.governorSpeedLimit;
    }
    return daOptions;
  }

  convertToStopLocations(stops: DaTrip.StopInfo[]): StopState[] {
    const stopStates = stops.map((stop) => {
      const location = stop.Location;
      const lng = Number.parseFloat(location.Coords.Lon);
      const lat = Number.parseFloat(location.Coords.Lat);
      const loc: StopLocation = {
        address: {
          streetAddress: location.Address.StreetAddress,
          city: location.Address.City,
          stateAbbreviation: location.Address.State,
          zip: location.Address.Zip,
          county: location.Address.County,
          countryAbbreviation: location.Address.Country,
        },
        coords: [lng, lat],
      };
      const stopState: StopState = {
        lngLat: [lng, lat],
        location: loc,
      };
      return stopState;
    });
    return stopStates;
  }

  convertToDALocations(stops: RouteStop[]): DaTrip.StopInfo[] {
    const daStops: DaTrip.StopInfo[] = stops.map((stop) => {
      const daStop: DaTrip.StopInfo = {
        Location: {
          Address: {
            StreetAddress: stop.address?.streetAddress,
            City: stop.address?.city,
            State: stop.address?.state,
            Zip: stop.address?.zip,
            County: stop.address?.county,
            Country: stop.address?.country,
            SPLC: stop.address?.SPLC,
            // CountryPostalFilter: PostCodeType,
            // AbbreviationFormat?: CountryAbbreviationType,
            // StateName?: string,
            // StateAbbreviation?: string,
            // CountryAbbreviation?: string,
          },
          Coords: {
            Lat: stop.lngLat[1],
            Lon: stop.lngLat[0],
          },
          // Region?: DataRegion;
          // Label?: string;
          // PlaceName?: string;
          // PlaceId?: string;
          // SpeedLimitHeading?: number;
          // SplitHouseNumber?: boolean;
          // IncludeTrimblePlaceIDs?: boolean;
          // FPCCode?: FPCGeocodes;
        },
        // Waypoint?: boolean;
        // stopType?: StopType;
      };
      return daStop;
    });

    return daStops;
  }

  convertToEnglish(profile: DaTrip.RoutingProfile) {
    // units are in feet/inches
    // TODO: below commented logic is for converting feetInchesToString, need to validate and remove.
    // const height = this.stringToFeetInches(profile.maxHeight.toString());
    // const length = this.stringToFeetInches(profile.totalLength.toString());

    // const heightFeet = height[0];
    // const heightInches = height[1];
    // const lengthFeet = length[0];
    // const lengthInches = length[1];

    // const widthEnglish = this.stringToTotalInches(
    //   profile.maxWidth.toString()
    // );
    // options.trkHeight = this.feetInchesToString([heightFeet, heightInches]);
    // options.trkLength = this.feetInchesToString([lengthFeet, lengthInches]);
    // options.trkWidth = this.totalInchesToString(widthEnglish, true);

    const weightPounds = profile.totalWeight;
    const height = profile.maxHeight;
    const length = profile.totalLength;
    const width = profile.maxWidth;
    const options: TrimbleMaps.RouteOptions = {
      trkWeight: weightPounds,
      trkLength: this.totalInchesToString(length, false),
      trkWidth: this.totalInchesToString(width, true),
      trkHeight: this.totalInchesToString(height, false),
    };
    return options;
  }
  convertToMetric(profile: DaTrip.RoutingProfile) {
    const height = this.stringToFeetInches(
      Math.floor(profile.maxHeight).toString()
    );
    const length = this.stringToFeetInches(
      Math.floor(profile.totalLength).toString()
    );
    const heightMeters = this.feetInchesToMeters(height[0], height[1], 4);
    const lengthMeters = this.feetInchesToMeters(length[0], length[1], 4);
    const widthEnglish = this.stringToTotalInches(
      Math.floor(profile.maxWidth).toString()
    );
    const widthMetric = this.inchesToMeters(widthEnglish, 2);
    const weightKilograms = this.poundsToKilograms(
      profile.totalWeight,
      undefined
    );
    const options: TrimbleMaps.RouteOptions = {
      trkHeight: heightMeters,
      trkLength: lengthMeters,
      trkWidth: widthMetric,
      trkWeight: weightKilograms,
    };
    if (!Array.isArray(profile?.totalLength)) {
      options.governorSpeedLimit =
        profile?.governorSpeedLimit !== null
          ? Math.floor(profile?.governorSpeedLimit * 1.6)
          : profile?.governorSpeedLimit; // miles to km
      options.elevLimit =
        profile?.elevationLimit !== null
          ? Math.floor(profile?.elevationLimit / 3.28)
          : profile?.elevationLimit; // feet to meters
    }
    return options;
  }

  getAxels(vehicleType: number) {
    let axles;
    if (vehicleType === 0) {
      axles = defaultDimensions?.def?.truck?.axles;
    } else if (vehicleType === 1) {
      axles = defaultDimensions?.def?.light?.axles;
    } else if (vehicleType === 2) {
      axles = defaultDimensions?.def?.car?.axles;
    }
    return axles;
  }
  convertDimensionMeterToInches(profile: DaTrip.RoutingProfile) {
    const weightPounds = profile.totalWeight;
    const height = profile.maxHeight;
    const length = profile.totalLength;
    const width = profile.maxWidth;
    const options: TrimbleMaps.RouteOptions = {
      trkWeight: this.kilogramsToPounds(weightPounds),
      trkLength: this.metersToInches(length),
      trkWidth: this.metersToInches(width),
      trkHeight: this.metersToInches(height),
    };
    return options;
  }

  convertDimensionInchesToCentimeter(profile: DaTrip.RoutingProfile) {
    const weightPounds = profile.totalWeight;
    const height = profile.maxHeight;
    const length = profile.totalLength;
    const width = profile.maxWidth;
    const options: TrimbleMaps.RouteOptions = {
      trkWeight: this.poundsToKilograms(weightPounds, undefined),
      trkLength: this.convertFromInchesToCentiMeter(length),
      trkWidth: this.convertFromInchesToCentiMeter(width),
      trkHeight: this.convertFromInchesToCentiMeter(height),
    };
    return options;
  }
  getRouteOptionSummary(routeState: RouteState): string {
    let summary = '';
    if (
      routeState?.optionsState?.vehicleType !== undefined && // eslint-disable-line
      routeState?.optionsState?.vehicleType !== null
    ) {
      summary += `${
        find(optionsUI.vehicleType, {
          value: routeState?.optionsState?.vehicleType,
        }).display
      }, `;
    }
    summary +=
      routeState?.optionsState?.afSetIDs?.length > 0
        ? 'Use Route Modifiers, '
        : '';
    summary += routeState?.optionsState?.tollDiscourage ? 'Avoid Tolls, ' : '';
    summary += routeState?.optionsState?.elevLimit
      ? `Elev. ${routeState?.optionsState?.elevLimit}, `
      : 'Elevation Discouraged, ';
    summary += routeState?.optionsState?.highwayOnly
      ? 'Highway Only, '
      : 'Use Local Streets, ';
    summary += routeState?.optionsState?.ferryDiscourage
      ? 'Ferry Discouraged, '
      : 'Ferry incl., ';
    // 0 to 6
    if (routeState?.optionsState?.sideOfStreetAdherence >= 0) {
      summary += 'Side of Street: ';
      summary += `${
        find(optionsUI.sideOfStreetAdherence, {
          value: routeState?.optionsState?.sideOfStreetAdherence,
        }).display
      }, `;
    }
    // summary += routeState?.optionsState?.hubRouting ? 'Hub, ' : ''; // hubRouting does not exist in the SDK
    summary += routeState?.optionsState?.useSites ? 'Site, ' : '';
    summary +=
      routeState?.optionsState?.overrideClass === 3
        ? "National Network, 53' Trailer, " // eslint-disable-line
        : '';
    summary += routeState?.optionsState?.bordersOpen
      ? 'Borders Open, '
      : 'Borders Closed, ';
    summary += routeState?.optionsState?.governorSpeedLimit
      ? 'Governor Speed Limit, '
      : '';
    summary +=
      routeState?.optionsState?.hazMatType > 0
        ? `${
            find(optionsUI.hazmat, {
              value: routeState?.optionsState?.hazMatType,
            }).display
          }, `
        : '';
    summary += routeState?.optionsState?.overrideRestrict ? 'Override, ' : '';
    if (
      routeState?.optionsState?.routeType !== undefined && // eslint-disable-line
      routeState?.optionsState?.routeType !== null
    ) {
      summary += `${
        find(optionsUI.routingType, {
          value: routeState?.optionsState?.routeType,
        }).display
      } Routing Type, `;
    }
    summary += routeState?.optionsState?.inclFerryDist
      ? 'Ferry Dist. incl, '
      : '';
    if (summary.length >= 2) {
      summary = summary.substring(0, summary.length - 2);
    }
    return summary;
  }
  getRoutingProfileSummary(
    routingProfile: RoutingProfileModel,
    routeOptions: RoutingProfile,
    routeState: RouteState
  ): string {
    let summary = '';
    if (routingProfile?.vehicleType) {
      summary += `${routingProfile?.vehicleType}, `;
    }
    if (
      this.userService?.getUser()?.region.toLowerCase() === Common.Region.NA &&
      this.userService?.getUser()?.subscriptionLevel !==
        SubscriptionLevel.STANDARD &&
      typeof routeState?.optionsState.inclTollData !== 'undefined'
        ? routeState?.optionsState.inclTollData
        : routeOptions?.includeTollData
    ) {
      summary += `${this.getEnumTextByValue(
        routingProfile?.routingOptions?.tollRoads,
        TollRoadTypeEnum
      )} Tolls,`;
    }
    summary += routingProfile?.routingOptions?.elevationLimit
      ? `Elev. ${routingProfile?.routingOptions?.elevationLimit}, `
      : 'Elevation Discouraged, ';
    summary += routeOptions?.highwayOnly
      ? 'Highway Only, '
      : 'Use Local Streets, ';
    summary +=
      routingProfile?.routingOptions?.ferries === 1
        ? 'Ferry Discouraged, '
        : 'Ferry incl., ';
    // based on default options
    if (routeOptions?.sideOfStreetAdherence >= 0) {
      summary += 'Side of Street: ';
      summary += `${
        find(optionsUI.sideOfStreetAdherence, {
          value: routeOptions?.sideOfStreetAdherence,
        }).display
      }, `;
    }
    summary += routingProfile?.routingOptions?.useOpenBorders
      ? 'Borders Open, '
      : 'Borders Closed, ';
    summary += routingProfile?.vehicleOptions?.governorSpeedLimit
      ? `Governor Speed Limit ${routingProfile?.vehicleOptions?.governorSpeedLimit}, `
      : '';
    summary +=
      routingProfile?.vehicleOptions?.hazmatType > 0
        ? `${this.getEnumTextByValue(
            routingProfile?.vehicleOptions?.hazmatType,
            HazmatTypeEnum
          )}, `
        : '';
    if (routingProfile?.routingOptions?.restrictionZones) {
      summary += this.iterateObject(
        routingProfile?.routingOptions?.restrictionZones
      );
    }

    summary += `${
      find(optionsUI.routingType, {
        value: routingProfile?.routingOptions?.routeType,
      })?.display
    } Routing Type, `;
    // based on default options
    summary += routeOptions?.includeFerryDistance ? 'Ferry Dist. incl, ' : '';
    if (summary.length >= 2) {
      summary = summary.substring(0, summary.length - 2);
    }
    return summary;
  }

  getEnumTextByValue(valueOperator, Operators): string {
    let operator: string;
    for (const [key, value] of Object.entries(Operators)) {
      value === valueOperator ? (operator = key) : null;
    }
    return operator;
  }
  iterateObject(restrictionZones: RestrictionZonesModel) {
    for (const zone in restrictionZones) {
      return restrictionZones[zone];
    }
  }

  // getDARouteOptionsFromPCM
  convertToDARouteOptionsFromRouteState(
    routeName: string,
    routingProfileId: number | null,
    routeState: RouteState,
    selectedProfile: RoutingProfileModel
  ): DaTrip.RoutingProfile {
    const daOptions: DaTrip.RoutingProfile = {
      vehicleType:
        routeState.optionsState.vehicleType ===
        TrimbleMaps.Common.VehicleType.AUTOMOBILE
          ? DaTrip.ProfileVehicleType.Auto
          : routeState.optionsState.vehicleType ===
            TrimbleMaps.Common.VehicleType.LIGHT_TRUCK
          ? DaTrip.ProfileVehicleType.LightDuty
          : DaTrip.ProfileVehicleType.Truck,
      congestionZone:
        routeState.optionsState.congestionZone ===
        TrimbleMaps.Common.ZoneType.ALLOW
          ? 0
          : routeState.optionsState.congestionZone ===
            TrimbleMaps.Common.ZoneType.WARN
          ? 1
          : 2,
      environmentalZone: null, // Todo-yz not in the SDK
      LCV: routeState.optionsState.trkLCV
        ? routeState.optionsState.trkLCV
        : false,
      VehicleGroups: [],
      classOverrides:
        routeState.optionsState.overrideClass ===
        TrimbleMaps.Common.ClassOverride.ALL
          ? TrimbleMaps.Common.ClassOverride.ALL
          : TrimbleMaps.Common.ClassOverride.NONE,
      costGHG: routeState.optionsState.costGHG?.toString(),
      costMaintEmpty: routeState.optionsState.costMaintEmpty?.toString(),
      costMaintLoad: routeState.optionsState.costMaintLoad?.toString(),
      costPerFuelUnit: routeState.optionsState.costPerFuelUnit?.toString(),
      costTimeEmpty: routeState.optionsState.costTimeEmpty?.toString(),
      costTimeLoad: routeState.optionsState.costTimeLoad?.toString(),
      dataVersion: routeState.optionsState.dataVersion,
      maxHeight: selectedProfile?.vehicleOptions?.vehicleConfig?.height,
      maxWidth: selectedProfile?.vehicleOptions?.vehicleConfig?.width,
      totalLength: selectedProfile?.vehicleOptions?.vehicleConfig?.length,
      totalWeight: selectedProfile?.vehicleOptions?.vehicleConfig?.weight,
      MaxWeightPerAxleGroup:
        selectedProfile?.vehicleOptions?.vehicleConfig?.maxWeightPerAxleGroup,
      displayRestrictions:
        routeState.optionsState.displayRestrictions ===
        TrimbleMaps.Common.DisplayRestrictionsType.ALWAY_OFF
          ? DaTrip.DisplayRestrictionsType.AlwaysOff
          : routeState.optionsState.displayRestrictions ===
            TrimbleMaps.Common.DisplayRestrictionsType.ALWAY_ON
          ? DaTrip.DisplayRestrictionsType.AlwaysOn
          : DaTrip.DisplayRestrictionsType.BasedOnTruckRestrictions,
      distanceUnits: routeState.optionsState.distanceUnits,
      exchangeRate: routeState.optionsState.exchangeRate?.toString(),
      useTraffic: routeState.optionsState.useTraffic,
      //ferryClosed: false,
      fiftyThreeFootTrailer:
        routeState.optionsState.overrideClass ===
        TrimbleMaps.Common.ClassOverride.ALL,
      fuelEconomyEmpty: routeState.optionsState.fuelEconEmpty?.toString(),
      fuelEconomyLoad: routeState.optionsState.fuelEconLoad?.toString(),
      fuelUnits: routeState.optionsState.fuelUnits,
      hazmatType: routeState.optionsState.hazMatType,
      highwayOnly: routeState.optionsState.highwayOnly
        ? routeState.optionsState.highwayOnly
        : false,
      includeFerryDistance: routeState.optionsState.inclFerryDist,
      nationalNetwork:
        routeState.optionsState.overrideClass ===
        TrimbleMaps.Common.ClassOverride.ALL,
      numAxles: routeState.optionsState.trkAxles,
      //propane: true,
      routingProfileId: routingProfileId, // The SDK, PCM doesn't have id. It has profile name.
      tollCurrency: routeState.optionsState.tollCurrency,
      tollDiscouraged: routeState.optionsState.tollDiscourage
        ? routeState.optionsState.tollDiscourage
        : false,
      tollRoadType:
        routeState.optionsState.tollRoads ||
        DaTrip.TollRoadsType.AvoidIfPossible, // IS REQUIRED
      // totalWeight: Number(routeState.optionsState.trkWeight), // The SDK trkWeight is number. The weight of the vehicle in pounds or kilograms depending on units.
      //totalWeightPerAxle: 0,
      truckDimensions: 16,
      unitsOfMeasure:
        routeState.optionsState.distanceUnits ===
        UnitsOfMeasurementEnum.Imperial
          ? DaTrip.VehicleDimUnits.English
          : DaTrip.VehicleDimUnits.Metric,
      name: routeName,
      bordersOpen: routeState.optionsState.bordersOpen
        ? routeState.optionsState.bordersOpen
        : false,
      routingType: routeState.optionsState.routeType,
      overrideRestrictions: routeState.optionsState.overrideRestrict,
      //hubRouting: routeState.optionsState.hubRouting, // The SDK doesn't support hubRouting
      includeTollData: routeState.optionsState.inclTollData,
      tollDiscountPlans: routeState.optionsState.inclTollData
        ? routeState.optionsState.tollPlan
        : '', // if toll data is false, put empty string.
      elevationLimit: routeState.optionsState.elevLimit, // Can't be null.
      isFerryDiscouraged: false, // Todo-yz: the SDK doesn't have routeState.optionsState.ferryDiscourage. After the SDK update, need to update this line.
      useSites: routeState.optionsState.useSites,
      governorSpeedLimit: routeState.optionsState.governorSpeedLimit, // Can't be null.
      sideOfStreetAdherence: routeState.optionsState.sideOfStreetAdherence,
      euProfile: {
        fuelType: routeState.optionsState.fuelType,
        refrigerated: routeState.optionsState.refrigerated,
        fuelConsumption: routeState.optionsState.fuelConsumption,
        emiType: routeState.optionsState
          .emiType as unknown as DaTrip.EUEmissionType,
        // hasTrailer: // Todo-yz: the SDK doesn't have routeState.optionsState.EUOptions.hasTrailer. I don't knwo what to do with it.
        isHybrid: routeState.optionsState.isHybrid,
        minPoll: routeState.optionsState.minPoll,
        trailerCount: routeState.optionsState.trlCount,
        trailerMaxAxles: routeState.optionsState.trlMaxAxles,
        trailerType:
          routeState.optionsState.trlType ===
          TrimbleMaps.Common.TrailerType.CARAVAN
            ? DaTrip.EUTrailerType.Caravan
            : routeState.optionsState.trlType ===
              TrimbleMaps.Common.TrailerType.TRAILER
            ? DaTrip.EUTrailerType.Trailer
            : DaTrip.EUTrailerType.None,
        trailerMaxWt: routeState.optionsState.trlMaxWt,
      },
    };

    // The web serivce is very picky on null or undefined values on the following properties.
    // Byte can't be null
    if (daOptions.environmentalZone === null) {
      delete daOptions.environmentalZone;
    }
    if (daOptions.elevationLimit === null || isNaN(daOptions.elevationLimit)) {
      delete daOptions.elevationLimit;
    }
    if (daOptions.totalWeight === null || isNaN(daOptions.totalWeight)) {
      delete daOptions.totalWeight;
    }
    if (
      daOptions.governorSpeedLimit === null ||
      isNaN(daOptions.governorSpeedLimit)
    ) {
      delete daOptions.governorSpeedLimit;
    }
    return daOptions;
  }

  convertToRoutingProfileModal(
    tripData: DaTrip.Trip,
    profile: RoutingProfileModel,
    isEuRegion: boolean
  ) {
    const profileOption: RoutingProfileModel = {
      name: tripData.Profile?.name, //new custom profile name
      routingProfileId: profile?.routingProfileId,
      vehicleType: this.convertToProfileVehicleTypeEnum(
        tripData?.Profile?.vehicleType
      ),
      units: isEuRegion
        ? UnitsOfMeasurementEnum.Metric
        : UnitsOfMeasurementEnum.Imperial,
      isAccountDefault: false,
      routingOptions: {
        ferries: tripData?.Profile?.isFerryDiscouraged
          ? FerriesEnum.Avoid
          : FerriesEnum.Use,
        routeType: tripData?.Profile
          ?.routingType as unknown as (typeof RoutingTypeEnum)[keyof typeof RoutingTypeEnum],
        tollRoads: tripData?.Profile
          ?.tollRoadType as unknown as (typeof TollRoadTypeEnum)[keyof typeof TollRoadTypeEnum],
        useOpenBorders: tripData?.Profile?.bordersOpen
          ? tripData?.Profile?.bordersOpen
          : false,
        sideOfStreetAdherenceLevel: tripData?.Profile
          ?.sideOfStreetAdherence as unknown as SideOfStreetAdherenceLevel,
        useStateAndNationalNetwork: tripData?.Profile?.classOverrides
          ? true
          : false,
        elevationLimit: tripData.Profile.elevationLimit
          ? tripData.Profile.elevationLimit
          : undefined,
      },
    };
    if (
      tripData.Profile?.totalLength &&
      tripData?.Profile?.vehicleType !== DaTrip.ProfileVehicleType.Auto
    ) {
      profileOption.vehicleOptions = {
        vehicleConfig: {
          height: tripData.Profile.maxHeight,
          width: tripData.Profile.maxWidth,
          weight: tripData.Profile.totalWeight,
          length: tripData.Profile.totalLength,
          maxWeightPerAxleGroup: tripData.Profile.MaxWeightPerAxleGroup
            ? tripData.Profile.MaxWeightPerAxleGroup
            : profile?.vehicleOptions?.vehicleConfig.maxWeightPerAxleGroup,
        },
      };
    }
    if (
      tripData.Profile?.totalLength &&
      tripData?.Profile?.vehicleType !== DaTrip.ProfileVehicleType.Auto
    ) {
      profileOption.vehicleOptions.governorSpeedLimit = tripData.Profile
        ?.governorSpeedLimit
        ? tripData.Profile.governorSpeedLimit
        : undefined;
      profileOption.vehicleOptions.hazmatType = tripData.Profile
        .hazmatType as unknown as (typeof HazmatTypeEnum)[keyof typeof HazmatTypeEnum];
      profileOption.vehicleOptions.tunnelCategory = TunnelCategoryEnum.NONE;
    }
    profileOption.copilotOptions = {
      displayRestrictions: DisplayRestrictionTypeEnum.BasedOnTruckRestrictions,
    };
    return profileOption;
  }

  createRoutingReportOptions(
    options: Omit<TrimbleMaps.RouteOptions, 'dataVersion'> & {
      dataVersion?: string;
    }
  ) {
    let truckCfg;
    if (
      options.trkLCV !== undefined ||
      options.trkUnits !== undefined ||
      options.trkHeight !== undefined ||
      options.trkLength !== undefined ||
      options.trkWidth !== undefined ||
      options.trkMaxWeightPerAxleGroup !== undefined ||
      options.trkTireCount !== undefined ||
      options.trkWeight !== undefined ||
      options.trkAxles !== undefined
    ) {
      truckCfg = {
        LCV: options.trkLCV,
        Units: options.trkUnits,
        Height: options.trkHeight,
        Length: options.trkLength,
        Width: options.trkWidth,
        Weight: options.trkWeight,
        Axles: options.trkAxles,
        MaxWeightPerAxleGroup: options.trkMaxWeightPerAxleGroup,
        TireCount: options.trkTireCount,
      };
    }

    let engine;
    if (
      options.isHybrid !== undefined ||
      options.emiType !== undefined ||
      options.minPoll !== undefined
    ) {
      engine = {
        Hybrid: options.isHybrid,
        EmissionType: options.emiType,
        MinPollutionVeh: options.minPoll,
      };
    }

    let trailerCfg;
    if (
      options.trlType !== undefined ||
      options.trlCount !== undefined ||
      options.trlMaxHt !== undefined ||
      options.trlMaxWt !== undefined ||
      options.trlMaxAxles !== undefined ||
      engine !== undefined
    ) {
      trailerCfg = {
        TypeOfTrailer: options.trlType,
        Count: options.trlCount,
        MaxHeight: options.trlMaxHt,
        MaxWeight: options.trlMaxWt,
        MaxAxles: options.trlMaxAxles,
        Engine: engine,
      };
    }

    let restrictionZones;
    if (
      options.congestionZone !== undefined ||
      options.lowEmissionZone !== undefined ||
      options.ultraLowEmissionZone !== undefined
    ) {
      restrictionZones = {
        CongestionZone: options.congestionZone,
        LowEmissionZone: options.lowEmissionZone,
        UltraLowEmissionZone: options.ultraLowEmissionZone,
      };
    }

    let customBreakDurations;
    if (
      options.hosShortBreakDuration !== undefined ||
      options.hosEndOfDutyBreakDuration !== undefined ||
      options.hosCycleResetBreakDuration !== undefined ||
      options.hosDriverSwitchBreakDuration !== undefined ||
      options.hosBorderCrossingWaitDuration !== undefined ||
      options.hosBreakTimeInDayDuration !== undefined ||
      options.hosBreakTimeInWeekDuration !== undefined
    ) {
      customBreakDurations = {
        ShortBreakDuration: options.hosShortBreakDuration,
        EndOfDutyBreakDuration: options.hosEndOfDutyBreakDuration,
        CycleResetBreakDuration: options.hosCycleResetBreakDuration,
        DriverSwitchBreakDuration: options.hosDriverSwitchBreakDuration,
        BorderCrossingWaitDuration: options.hosBorderCrossingWaitDuration,
        BreakTimeInDayDuration: options.hosBreakTimeInDayDuration,
        BreakTimeInWeekDuration: options.hosBreakTimeInWeekDuration,
      };
    }

    let hosOptions;
    if (
      options.hosEnabled !== undefined ||
      options.hosRemDriveTimeUntilBreak !== undefined ||
      options.hosRemDriveTime !== undefined ||
      options.hosRemOnDutyTime !== undefined ||
      options.hosRuleType !== undefined ||
      options.hosRemCycleDutyTime !== undefined ||
      options.hosCycleTimeGains !== undefined ||
      options.hosLastCycleResetDateTime !== undefined ||
      options.hosLast24HourBreakDateTime !== undefined ||
      options.hosRemainingCanadaOnDutyTime !== undefined ||
      options.hosRemainingDriveTimeUntilBreakDriver2 !== undefined ||
      options.hosRemainingDriveTimeDriver2 !== undefined ||
      options.hosRemainingOnDutyTimeDriver2 !== undefined ||
      options.hosRemainingCycleDutyTimeDriver2 !== undefined ||
      options.hosCycleTimeGainsDriver2 !== undefined ||
      options.hosLastCycleResetDateTimeDriver2 !== undefined ||
      options.hosLast24HourBreakDateTimeDriver2 !== undefined ||
      options.hosRemainingCanadaOnDutyTimeDriver2 !== undefined ||
      options.hosAlternateHoSRuleType !== undefined ||
      options.hosRemainingTimeUntilFullDayBreak !== undefined ||
      options.hosRemainingTimeUntilFullDayBreakDriver2 !== undefined ||
      options.hosRemainingDrivingTimeInWeek !== undefined ||
      options.hosRemainingOnDutyTimeInDay !== undefined ||
      options.hosRemainingConsecutiveDrivingTime !== undefined ||
      options.hosRemainingFortnightlyTime !== undefined ||
      options.hosCurrentOffDutyTime !== undefined ||
      options.hosCurrentOffDutyTimeDriver2 !== undefined ||
      options.hosUseCycleReset !== undefined ||
      options.hosTerminalDayStartTime !== undefined ||
      options.hosUseCustomPlaces !== undefined ||
      options.hosTeamDriving !== undefined ||
      customBreakDurations !== undefined ||
      options.hosMaxAlternateRestStops !== undefined ||
      options.hosSkipRestStopInsertion !== undefined ||
      options.hosActiveDriver !== undefined ||
      options.hosMaxDrivingTimeInDay !== undefined ||
      options.hosMaxOnDutyTimeInDay !== undefined ||
      options.hosMaxOnDutyTimeInWeek !== undefined ||
      options.hosAdverseConditionException !== undefined ||
      options.hosPOIStopsReturned !== undefined ||
      options.hosHOSWindowStart !== undefined ||
      options.hosHOSWindowEnd !== undefined
    ) {
      hosOptions = {
        Enabled: options.hosEnabled,
        RemainingDriveTimeUntilBreak: options.hosRemDriveTimeUntilBreak,
        RemainingDriveTime: options.hosRemDriveTime,
        RemainingOnDutyTime: options.hosRemOnDutyTime,
        HoSRuleType: options.hosRuleType,
        RemainingCycleDutyTime: options.hosRemCycleDutyTime,
        CycleTimeGains: options.hosCycleTimeGains,
        LastCycleResetDateTime: options.hosLastCycleResetDateTime,
        Last24HourBreakDateTime: options.hosLast24HourBreakDateTime,
        RemainingCanadaOnDutyTime: options.hosRemainingCanadaOnDutyTime,
        RemainingDriveTimeUntilBreakDriver2:
          options.hosRemainingDriveTimeUntilBreakDriver2,
        RemainingDriveTimeDriver2: options.hosRemainingDriveTimeDriver2,
        RemainingOnDutyTimeDriver2: options.hosRemainingOnDutyTimeDriver2,
        RemainingCycleDutyTimeDriver2: options.hosRemainingCycleDutyTimeDriver2,
        CycleTimeGainsDriver2: options.hosCycleTimeGainsDriver2,
        LastCycleResetDateTimeDriver2: options.hosLastCycleResetDateTimeDriver2,
        Last24HourBreakDateTimeDriver2:
          options.hosLast24HourBreakDateTimeDriver2,
        RemainingCanadaOnDutyTimeDriver2:
          options.hosRemainingCanadaOnDutyTimeDriver2,
        AlternateHoSRuleType: options.hosAlternateHoSRuleType,
        RemainingTimeUntilFullDayBreak:
          options.hosRemainingTimeUntilFullDayBreak,
        RemainingTimeUntilFullDayBreakDriver2:
          options.hosRemainingTimeUntilFullDayBreakDriver2,
        RemainingDrivingTimeInWeek: options.hosRemainingDrivingTimeInWeek,
        RemainingOnDutyTimeInDay: options.hosRemainingOnDutyTimeInDay,
        RemainingConsecutiveDrivingTime:
          options.hosRemainingConsecutiveDrivingTime,
        RemainingFortnightlyTime: options.hosRemainingFortnightlyTime,
        CurrentOffDutyTime: options.hosCurrentOffDutyTime,
        CurrentOffDutyTimeDriver2: options.hosCurrentOffDutyTimeDriver2,
        UseCycleReset: options.hosUseCycleReset,
        TerminalDayStartTime: options.hosTerminalDayStartTime,
        UseCustomPlaces: options.hosUseCustomPlaces,
        TeamDriving: options.hosTeamDriving,
        CustomBreakDurations: customBreakDurations,
        MaxAlternateRestStops: options.hosMaxAlternateRestStops,
        SkipRestStopInsertion: options.hosSkipRestStopInsertion,
        ActiveDriver: options.hosActiveDriver,
        MaxDrivingTimeInDay: options.hosMaxDrivingTimeInDay,
        MaxOnDutyTimeInDay: options.hosMaxOnDutyTimeInDay,
        MaxOnDutyTimeInWeek: options.hosMaxOnDutyTimeInWeek,
        AdverseConditionException: options.hosAdverseConditionException,
        POIStopsReturned: options.hosPOIStopsReturned,
        HOSWindowStart: options.hosHOSWindowStart,
        HOSWindowEnd: options.hosHOSWindowEnd,
      };
    }
    // let routeOptimization;
    // if (options.hubRouting !== true) {
    //   routeOptimization = options.routeOptimization;
    // }
    const afsetIds = this.userService.getSelectedAFSets()?.map((af) => {
      return af;
    });
    // const afsetNames = this.userService.getSelectedAFSets()?.map((af) => {
    //   return af.name;
    // });
    const opts = {
      VehicleType: options.vehicleType,
      RoutingType: options.routeType,
      // HubRouting: options.hubRouting,
      RouteOptimization: TrimbleMaps.Common.RouteOptimization.NONE, //routeOptimization,
      TollDiscourage: options.tollDiscourage,
      TollRoads: options.tollRoads,
      AFSetIDs: afsetIds,
      AFSetNames: [],
      BordersOpen: options.bordersOpen,
      OverrideRestrict: options.overrideRestrict,
      HighwayOnly: options.highwayOnly,
      HazMatType: options.hazMatType,
      HazMatTypes: options.hazMatTypes,
      // TunnelCategories: options.tunnelCategories,
      CustomRoadSpeeds: options.customRoadSpeeds,
      DistanceUnits: options.distanceUnits,
      TruckCfg: truckCfg,
      // CiteInterval: options.citeInt, // for geotunnel report, but no geotunnel report is exposed.
      ElevLimit: options.elevLimit,
      FerryDiscourage: options.ferryDiscourage,
      HoSOptions: hosOptions,
      MaintainHeading: options.maintainHeading,
      SideOfStreetAdherence: options.sideOfStreetAdherence,
      GovernorSpeedLimit: options.governorSpeedLimit,
      UseAvoidsAndFavors: options.useAvoidsAndFavors,
      UseSites: options.useSites,
      VehicleProfile: options.vehicleProfile,
      Refrigerated: options.refrigerated,
      FuelType: options.fuelType,
      FuelConsumption: options.fuelConsumption,
      TrafficLookaheadDistance: options.trafficLookaheadDistance,
      AvoidState: options.avoidState,
      AvoidCountry: options.avoidCountry,
      FavorState: options.favorState,
      FavorCountry: options.favorCountry,
      IncludeTrimblePlaceIDs: options.includeTrimblePlaceIDs,
      useCrossStreetTurnAvoidance: options.useCrossStreetTurnAvoidance, // starts with lower case u.
      CongestionZonesOpen: options.congestionZonesOpen,
      LowEmissionZonesOpen: options.lowEmissionZonesOpen,
      DisplayRestrictions: options.displayRestrictions,
      profileName: options.profileName, // starts with lower case p. Null value does not cause error.
      profileId: options?.profileId,
      TrailerCfg: options.useEUTollsReport ? trailerCfg : undefined, // this is only used for EU TollDetail report
      RestrictionZones: restrictionZones,
      ClassOverrides: options.overrideClass,
    };
    return opts;
  }
}
